<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useConfig } from 'dashboard/composables/useConfig';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  activePanel: {
    type: String,
    required: true,
    validator: value => ['details', 'copilot'].includes(value),
  },
});

const { t } = useI18n();
const { updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Mirror CopilotContainer's availability: without it the tab is a dead end.
const showCopilotTab = computed(
  () =>
    isEnterprise &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.CAPTAIN
    )
);

const tabs = computed(() => [
  { key: 'details', label: t('CONVERSATION.SIDEBAR.DETAILS') },
  { key: 'copilot', label: t('CONVERSATION.SIDEBAR.COPILOT') },
]);

const activeTabIndex = computed(() =>
  props.activePanel === 'copilot' ? 1 : 0
);

const onTabChanged = tab => {
  updateUISettings({
    is_contact_sidebar_open: tab.key === 'details',
    is_copilot_panel_open: tab.key === 'copilot',
  });
};

// Closing lives with the tab switcher rather than inside either panel body:
// this row is the only chrome both panels share, so the control does not move
// as the agent switches between Details and Tola AI. Same write the panel
// bodies used to make - both flags off closes the sidepanel outright.
const closePanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 flex-shrink-0 px-3 py-2 border-b border-n-weak"
  >
    <TabBar
      v-if="showCopilotTab"
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      @tab-changed="onTabChanged"
    />
    <span v-else class="py-1 text-sm font-medium text-n-slate-12">
      {{ t('CONVERSATION.SIDEBAR.DETAILS') }}
    </span>
    <Button
      v-tooltip="t('GENERAL.CLOSE')"
      :aria-label="t('GENERAL.CLOSE')"
      icon="i-lucide-panel-right-close"
      ghost
      sm
      class="flex-shrink-0 rtl:-scale-x-100"
      @click="closePanel"
    />
  </div>
</template>
